import { createRoute, redirect, useLoaderData } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import { pdpPageQueryOptions } from "~/app/api/pageData/pdpPageQuery";
import { sanitizePageDataSearch } from "~/app/api/pageData/pageDataClient";
import type { PdpPageInitialData } from "~/app/api/pageData/pdpPageInitialData";
import type { ProductListPageInitialData } from "~/app/api/pageData/productListPageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import { createProductDetailsPage } from "~/pages/ProductDetailsPage/ProductDetailsPage";
import { loadPlpPageData, ProductListRouteContent } from "./plpRoute";

export const SHORT_PDP_ROUTE_PATH = "/p/$productCode";

export type RawProductRouteParams = {
  _splat?: string;
};

export type ProductRouteTarget = { pageType: "pdp"; productCode: string } | { pageType: "plp"; categoryCode: string };

export type ProductRouteLoaderData =
  | { pageType: "pdp"; result: PageInitialData<PdpPageInitialData> }
  | { pageType: "plp"; result: PageInitialData<ProductListPageInitialData> };

export function extractProductRouteTarget(splat: string | undefined): ProductRouteTarget | null {
  const productMatch = splat?.match(/(?:^|\/)p\/([^/]+)$/);
  if (productMatch) {
    return { pageType: "pdp", productCode: productMatch[1] };
  }

  const categoryMatch = splat?.match(/(?:^|\/)c\/([^/]+)$/);
  if (categoryMatch) {
    return { pageType: "plp", categoryCode: categoryMatch[1] };
  }

  return null;
}

export function getProductRouteTarget(params: RawProductRouteParams) {
  const target = extractProductRouteTarget(params._splat);

  if (!target) {
    throw new Error("Product route did not include a supported /p/{productCode} or /c/{categoryCode} tail.");
  }

  return target;
}

function ShortPdpRoute() {
  const result = useLoaderData({
    from: SHORT_PDP_ROUTE_PATH,
  }) as PageInitialData<PdpPageInitialData>;

  return <ProductDetailsRouteContent result={result} />;
}

export function ProductRouteContent({ page }: { page: ProductRouteLoaderData }) {
  if (page.pageType === "plp") {
    return <ProductListRouteContent result={page.result} />;
  }

  return <ProductDetailsRouteContent result={page.result} />;
}

function ProductDetailsRouteContent({ result }: { result: PageInitialData<PdpPageInitialData> }) {
  const shellProps = useStorefrontShellProps();
  const data = result.data;

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {data
        ? createProductDetailsPage({
            product: data.product,
            technicalInfo: data.technicalInfo ?? null,
            calculationTabScriptUrl: data.calculationTabScriptURL ?? "",
            showFBT: Boolean(data.showFBT),
            displayMMDAssistant: Boolean(data.displayMMDAssistant),
            mountingMgrWebComponentUrl: data.mountingMgrWebComponentUrl ?? "",
            greaseAppWebComponentUrl: data.greaseAppWebComponentUrl ?? "",
            bearingXUrl: data.bearingXUrl ?? "",
          })
        : null}
    </StorefrontShellFrame>
  );
}

export async function loadPdpPageData(
  context: { queryClient: QueryClient },
  productCode: string,
  location: { pathname: string; searchStr: string } = { pathname: `/p/${productCode}`, searchStr: "" },
) {
  const result = await context.queryClient.ensureQueryData(pdpPageQueryOptions(productCode, location.searchStr));
  const page = unwrapPageInitialData(result);
  const canonicalLocation = page.data.product.url;
  const browserSearch = sanitizePageDataSearch(location.searchStr);

  if (
    canonicalLocation &&
    (!equivalentEncodedPaths(location.pathname, pathnameOf(canonicalLocation)) || browserSearch !== location.searchStr)
  ) {
    throw redirect({
      href: appendSearch(canonicalLocation, browserSearch),
      statusCode: 301,
    });
  }

  return page;
}

function pathnameOf(location: string): string {
  try {
    return new URL(location, window.location.origin).pathname;
  } catch {
    return location.split("?", 1)[0];
  }
}

function equivalentEncodedPaths(left: string, right: string): boolean {
  const normalize = (path: string) => {
    const contextFreePath = withoutFrontendContext(path);
    let decodedPath = contextFreePath;
    try {
      decodedPath = decodeURI(contextFreePath);
    } catch {
      // Keep malformed escape sequences intact so the canonical comparison can
      // fail safely instead of breaking route loading.
    }
    return decodedPath.replace(/%[0-9a-f]{2}/gi, (escape) => escape.toUpperCase());
  };
  return normalize(left) === normalize(right);
}

function withoutFrontendContext(path: string): string {
  const contextPath = window.__SCHA_BOOT__?.contextPath?.replace(/\/$/, "") ?? "";
  if (!contextPath || contextPath === "/") {
    return path;
  }
  if (path === contextPath) {
    return "/";
  }
  return path.startsWith(`${contextPath}/`) ? path.slice(contextPath.length) : path;
}

function appendSearch(location: string, search: string): string {
  if (!search) {
    return location;
  }
  return `${location}${location.includes("?") ? "&" : "?"}${search.slice(1)}`;
}

export async function loadProductRoutePageData(
  context: { queryClient: QueryClient },
  params: RawProductRouteParams,
  location: { pathname: string; searchStr: string } = {
    pathname: params._splat ? `/${params._splat}` : "/",
    searchStr: "",
  },
): Promise<ProductRouteLoaderData> {
  const target = getProductRouteTarget(params);

  if (target.pageType === "plp") {
    return {
      pageType: "plp",
      result: await loadPlpPageData(context, target.categoryCode, location),
    };
  }

  return {
    pageType: "pdp",
    result: await loadPdpPageData(context, target.productCode, location),
  };
}

export const shortPdpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: SHORT_PDP_ROUTE_PATH,
  loader: async ({ context, params, location }) => loadPdpPageData(context, params.productCode, location),
  component: ShortPdpRoute,
});
